import { getImageUrl } from './apiHelpers';
import { PRODUCT_PLACEHOLDER_IMAGE } from './constants';

/**
 * Parse une valeur de stock (peut arriver en number ou en string depuis l'API).
 * Retourne un entier >= 0 (0 inclus = rupture, valeur valide) ou null si inconnu.
 */
export const parseStock = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
};

/**
 * Extrait l'image d'un produit selon plusieurs priorités
 * @param {Object} product - L'objet produit
 * @param {number} selectedImageIndex - Index de l'image sélectionnée (par défaut: 0)
 * @param {string|null} imageUrl - URL d'image fournie directement (priorité absolue)
 * @returns {string} URL affichable (placeholder si aucune image)
 */
export const extractProductImage = (product, selectedImageIndex = 0, imageUrl = null) => {
  // Si une URL est fournie directement, l'utiliser
  if (imageUrl) {
    return getImageUrl(imageUrl) || PRODUCT_PLACEHOLDER_IMAGE;
  }

  if (!product || typeof product !== 'object') {
    return PRODUCT_PLACEHOLDER_IMAGE;
  }

  // Priorité 1: imageUrls avec index sélectionné
  if (product.imageUrls && Array.isArray(product.imageUrls) && product.imageUrls.length > 0) {
    const index = selectedImageIndex >= 0 && selectedImageIndex < product.imageUrls.length 
      ? selectedImageIndex 
      : 0;
    return getImageUrl(product.imageUrls[index] || product.imageUrls[0]);
  }

  // Priorité 2: images avec index sélectionné
  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    const index = selectedImageIndex >= 0 && selectedImageIndex < product.images.length 
      ? selectedImageIndex 
      : 0;
    return getImageUrl(product.images[index] || product.images[0]);
  }

  // Priorité 3: imageUrls (dernière image)
  if (product.imageUrls && product.imageUrls.length > 0) {
    return getImageUrl(product.imageUrls[product.imageUrls.length - 1]);
  }

  // Priorité 4: image_url
  if (product.image_url) {
    return getImageUrl(product.image_url);
  }

  // Priorité 5: image
  if (product.image) {
    return getImageUrl(product.image) || PRODUCT_PLACEHOLDER_IMAGE;
  }

  return PRODUCT_PLACEHOLDER_IMAGE;
};

/**
 * Image légère (vignette) d'un produit pour les LISTES / GRILLES (beaucoup de produits).
 * Priorité au tableau `thumbnailUrls` (généré côté backend, léger), avec fallback
 * automatique sur `imageUrls` pour les produits créés avant cette fonctionnalité.
 * Par défaut on prend la dernière image (cohérent avec l'affichage des cartes produit).
 * @param {Object} product
 * @param {number} selectedImageIndex - index précis si fourni (sinon dernière image)
 * @returns {string}
 */
export const extractProductThumbnail = (product, selectedImageIndex = -1) => {
  if (!product || typeof product !== 'object') {
    return PRODUCT_PLACEHOLDER_IMAGE;
  }

  const pickFrom = (arr) => {
    if (Array.isArray(arr) && arr.length > 0) {
      const index =
        selectedImageIndex >= 0 && selectedImageIndex < arr.length
          ? selectedImageIndex
          : arr.length - 1;
      return getImageUrl(arr[index] || arr[0]);
    }
    return null;
  };

  return (
    pickFrom(product.thumbnailUrls) ||
    pickFrom(product.imageUrls) ||
    pickFrom(product.images) ||
    (product.image_url ? getImageUrl(product.image_url) : null) ||
    (product.image ? getImageUrl(product.image) : null) ||
    PRODUCT_PLACEHOLDER_IMAGE
  );
};

/**
 * Prix unitaire affiché / panier : promoPrice uniquement si défini et strictement supérieur à 0 (après parsing nombre).
 * Sinon basePrice (ou price). Chaînes "0", null, undefined sont ignorées au profit du prix de base.
 * @param {Object} product - L'objet produit
 * @returns {number}
 */
export const getEffectiveProductPrice = (product) => {
  if (!product || typeof product !== 'object') return 0;

  const rawBase = product.basePrice ?? product.price ?? 0;
  const baseNum = typeof rawBase === 'number' ? rawBase : Number(rawBase);
  const safeBase = Number.isFinite(baseNum) ? baseNum : 0;

  const promo = product.promoPrice;
  if (promo === null || promo === undefined || promo === '') {
    return safeBase;
  }
  const promoNum = typeof promo === 'number' ? promo : Number(promo);
  if (!Number.isFinite(promoNum) || promoNum <= 0) {
    return safeBase;
  }
  return promoNum;
};

/**
 * Prix d'une ligne panier : prix ligne si supérieur à 0, sinon prix effectif du produit embarqué.
 * @param {Object} item - Item panier (invité ou réponse API)
 * @returns {number}
 */
export const resolveCartLineUnitPrice = (item) => {
  if (!item || typeof item !== 'object') return 0;
  const line = item.price;
  const lineNum =
    line !== null && line !== undefined && line !== '' ? Number(line) : NaN;
  if (Number.isFinite(lineNum) && lineNum > 0) {
    return lineNum;
  }
  if (item.product) {
    return getEffectiveProductPrice(item.product);
  }
  return Number.isFinite(lineNum) ? lineNum : 0;
};

/**
 * Extrait le prix d'un produit selon les priorités (alias historique)
 * @param {Object} product - L'objet produit
 * @returns {number} Prix du produit (0 par défaut)
 */
export const extractProductPrice = (product) => getEffectiveProductPrice(product);

/**
 * Extrait le nom d'un produit
 * @param {Object} product - L'objet produit
 * @returns {string} Nom du produit
 */
export const extractProductName = (product) => {
  return product.name || 'Produit sans nom';
};

/**
 * Extrait l'ID d'un produit
 * @param {Object} product - L'objet produit
 * @returns {string|null} ID du produit ou null
 */
export const extractProductId = (product) => {
  return product._id || product.id || null;
};

/**
 * Normalise un produit en item de panier
 * @param {Object} product - L'objet produit
 * @param {Object} options - Options supplémentaires
 * @returns {Object|null} Item de panier normalisé ou null
 */
export const normalizeProductToCartItem = (product, options = {}) => {
  const {
    quantity = 1,
    selectedImageIndex = 0,
    variantId = null,
    optionKey = null,
    imageUrl = null,
    itemId = null, // ID de l'item depuis le serveur (pour les items existants)
  } = options;

  const productId = extractProductId(product);
  if (!productId) {
    console.error('Impossible de normaliser le produit: ID manquant');
    return null;
  }


  const shopName = product.shop?.shop_name || '';
  // On enregistre le statut du produit au moment de l'ajout pour pouvoir
  // afficher "En rupture de stock" sur le panier des utilisateurs invités
  // (qui n'ont pas de panier serveur pour rafraîchir le statut).
  const status = product.status || null;
  const stock = parseStock(product.quantity);
  const isUnavailable = status === 'outofstock' || stock === 0;

  return {
    productId,
    name: extractProductName(product),
    quantity: parseInt(quantity, 10),
    price: extractProductPrice(product),
    // Vignette pour l'affichage panier (léger) ; respecte une URL fournie directement.
    image: imageUrl
      ? getImageUrl(imageUrl)
      : extractProductThumbnail(product, selectedImageIndex),
    // Stock disponible (0 inclus = rupture). null si inconnu.
    stock,
    shopName,
    shopId: product?.shopId || product?.shop?._id || product?.shop?.id || null,
    basePrice: Number(product?.basePrice) || Number(product?.price) || 0,
    variantId,
    optionKey,
    itemId, // Pour les items synchronisés depuis l'API
    status,
    isUnavailable,
  };
};

/**
 * Extrait le tableau d'articles depuis la réponse Axios de GET /users/:id/cart.
 * L'API peut renvoyer { items } ou { data: { items } } dans le corps.
 */
export const getCartItemsArrayFromCartApiResponse = (axiosResponse) => {
  if (!axiosResponse?.data) return [];
  const raw =
    axiosResponse.data?.items ?? axiosResponse.data?.data?.items;
  return Array.isArray(raw) ? raw : [];
};

/**
 * Normalise un item du serveur en item de panier local
 * @param {Object} serverItem - Item depuis l'API
 * @returns {Object|null} Item de panier normalisé ou null
 */
/** URL d’image pour une ligne panier (placeholder produit si manquante ou indisponible). */
export const resolveCartItemImageSrc = (item) => {
  const raw =
    item?.image ??
    item?.product?.thumbnailUrls?.[0] ??
    item?.product?.imageUrls?.[0];

  if (item?.isUnavailable && !raw) {
    return PRODUCT_PLACEHOLDER_IMAGE;
  }

  const url = getImageUrl(raw);
  return url || PRODUCT_PLACEHOLDER_IMAGE;
};

export const normalizeServerItemToCartItem = (serverItem) => {
  if (!serverItem?.productId) {
    return null;
  }

  // Un produit est "indisponible" si :
  //  - il n'existe plus côté serveur (product === null ou undefined), OU
  //  - son statut a été passé en "outofstock" par le vendeur.
  const productMissing =
    serverItem.product === null || serverItem.product === undefined;
  const productOutOfStock = serverItem.product?.status === 'outofstock';
  const stock = parseStock(serverItem.product?.quantity);
  const isUnavailable = productMissing || productOutOfStock || stock === 0;

  return {
    productId: serverItem.productId,
    name: serverItem.name || serverItem.product?.name || 'Produit',
    quantity: serverItem.quantity || 1,
    price: resolveCartLineUnitPrice(serverItem),
    image:
      serverItem.image ||
      serverItem.product?.thumbnailUrls?.[0] ||
      serverItem.product?.imageUrls?.[0] ||
      null,
    // Stock disponible (0 inclus = rupture). null si inconnu.
    stock,
    shopId: serverItem.product?.shopId || null,
    basePrice: Number(serverItem.product?.basePrice) || 0,
    variantId: serverItem.variantId || null,
    optionKey: serverItem.optionKey || null,
    itemId: serverItem._id || serverItem.id || null, // ID de l'item du serveur
    status: serverItem.product?.status || null,
    isUnavailable,
  };
};

